import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import { useTheme } from '../../contexts/ThemeContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TimelineCard from './components/TimelineCard';
import SkillEvolution from './components/SkillEvolution';
import CareerStats from './components/CareerStats';
import TechnologyEvolution from './components/TechnologyEvolution';
import TimelineNavigation from './components/TimelineNavigation';

const ExperienceCareerJourneyTimeline = () => {
  const { isDark } = useTheme();
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState('timeline');

  // Real experience data
  const experiencesData = [
    {
      id: 1,
      company: "Techdoz (Software Engineer)",
      companyLogo: "/assets/Experience/Techdoz.png",
      position: "Software Engineer",
      duration: "April 2025 - Present",
      type: "Full-time",
      location: "Remote",
      summary: "Developing a comprehensive recruitment web application with advanced features including secure authentication, CV analysis with NLP, performance scoring, and quiz system for skill assessment.",
      teamSize: "Development team",
      teamInfo: "Working on full-stack development using modern technologies with focus on user experience and security.",
      technologies: ["Flask", "React.js", "NLTK", "MySQL", "AWS"],
      achievements: [
        "Developed using Flask (backend), React.js (frontend), NLTK (NLP tasks), MySQL (database), and AWS (deployment)",
        "Implemented secure user authentication with CV upload/analysis capabilities",
        "Built performance scoring system and quiz functionality for skill assessment",
        "Created responsive admin interface based on CV analysis and quiz results"
      ],
      projects: [
        {
          name: "Recruitment Web Application",
          description: "Full-featured recruitment platform with CV analysis, candidate screening, and skill assessment",
          technologies: ["Flask", "React.js", "NLTK", "MySQL", "AWS"],
          impact: "Streamlined recruitment process with automated CV screening"
        }
      ],
      recommendation: {
        quote: "Lahiru has demonstrated exceptional technical skills in building our recruitment platform with innovative NLP-based CV analysis features.",
        author: "Techdoz Team",
        position: "Project Lead"
      }
    },
    {
      id: 2,
      company: "Sri Lanka Institute of Biotechnology (SLIBTEC)",
      companyLogo: "/assets/Experience/Sri Lanka Institute of Biotechnology (SLIBTEC).png",
      position: "Associate Software Engineer",
      duration: "June 2024 - April 2025",
      type: "Full-time",
      location: "Homagama, Sri Lanka",
      summary: "Enhanced and upgraded the SLIBTEC Inventory Management System with improved architecture, advanced features, and better security. Led system improvements for scalability, performance, and transparency.",
      teamSize: "Software development team",
      teamInfo: "Worked on system architecture enhancement using PHP, JavaScript, REST API, and MySQL.",
      technologies: ["PHP", "JavaScript", "REST API", "MySQL"],
      achievements: [
        "Enhanced system architecture using PHP, JavaScript, REST API, and MySQL for improved scalability and security",
        "Added advanced reporting, automated alerts, and audit trails for increased transparency",
        "Implemented process automation for streamlined procurement and stock replenishment",
        "Improved UX/UI by redesigning the interface for intuitive and user-friendly inventory data management"
      ],
      projects: [
        {
          name: "SLIBTEC Inventory System 2025",
          description: "System upgrade with enhanced architecture, advanced reporting, automated alerts, and improved UX/UI",
          technologies: ["PHP", "JavaScript", "REST API", "MySQL"],
          impact: "Reduced manual errors and boosted efficiency significantly"
        }
      ]
    },
    {
      id: 3,
      company: "Sri Lanka Institute of Biotechnology (SLIBTEC)",
      companyLogo: "/assets/Experience/Sri Lanka Institute of Biotechnology (SLIBTEC).png",
      position: "Intern Software Engineer",
      duration: "December 2023 - June 2024",
      type: "Internship",
      location: "Homagama, Sri Lanka",
      summary: "Designed and implemented a comprehensive inventory management system for SLIBTEC using PHP, JavaScript, CSS, REST API, Bootstrap, MySQL, and server configuration.",
      teamSize: "Development team",
      teamInfo: "Worked on full-stack development with focus on creating efficient inventory management solutions.",
      technologies: ["PHP", "JavaScript", "CSS", "REST API", "Bootstrap", "MySQL"],
      achievements: [
        "Designed and implemented the inventory management system using PHP, JavaScript, CSS, REST API, Bootstrap, and MySQL",
        "Managed inventories for key categories: Admin, Finance, HR, Procurement, Engineering, Business, Research, User",
        "Streamlined inventory processes to improve efficiency and accuracy",
        "Integrated a user-friendly interface for seamless access and management of inventory data"
      ],
      projects: [
        {
          name: "SLIBTEC Inventory Management System 2024",
          description: "Complete inventory management solution with category-based access control and user-friendly interface",
          technologies: ["PHP", "JavaScript", "Bootstrap", "MySQL", "REST API"],
          impact: "Streamlined inventory management across 8 departments"
        }
      ]
    },
    {
      id: 4,
      company: "YouTube Content Creator",
      companyLogo: "/assets/Experience/YouTube.png",
      position: "Technical Content Creator",
      duration: "March 2022 - Present",
      type: "Part-time",
      location: "Remote",
      summary: "Creating educational content and project tutorials, sharing knowledge about full-stack development, software engineering concepts, and real-world project implementations.",
      teamSize: "Individual contributor",
      teamInfo: "Creating and sharing technical content independently to help the developer community.",
      technologies: ["Multiple Technologies", "Video Production", "Technical Documentation"],
      achievements: [
        "Building a community of developers through educational content",
        "Sharing project implementations and best practices",
        "Creating tutorials on full-stack development",
        "Contributing to the tech education ecosystem"
      ],
      projects: [
        {
          name: "Full-Stack Online Job Portal System",
          description: "Comprehensive tutorial series on building job portal with React.js, Spring Boot, MySQL, and Apache Kafka",
          technologies: ["React.js", "Spring Boot", "MySQL", "Kafka"],
          impact: "Educated developers on real-time job portal implementation"
        },
        {
          name: "Point-Of-Sale (POS) Systems",
          description: "Tutorial on building POS system using PHP, MySQL, and AJAX with real-time communication",
          technologies: ["PHP", "MySQL", "AJAX", "Chart.js"],
          impact: "Demonstrated practical application of web technologies"
        }
      ]
    }
  ];

  // Real career statistics
  const careerStatsData = [
    {
      icon: "Calendar",
      value: "5+",
      label: "Years Experience",
      bgColor: "bg-primary/10",
      iconColor: "text-primary",
      growth: "Since 2020"
    },
    {
      icon: "Building",
      value: "2",
      label: "Companies",
      bgColor: "bg-success/10",
      iconColor: "text-success"
    },
    {
      icon: "Code",
      value: "14+",
      label: "Major Projects",
      bgColor: "bg-warning/10",
      iconColor: "text-warning",
      growth: "Web, Mobile, ML"
    },
    {
      icon: "Users",
      value: "Active",
      label: "Community Contributor",
      bgColor: "bg-brand-purple/10",
      iconColor: "text-brand-purple"
    }
  ];

  // Mock skills evolution data
  const skillsEvolutionData = [
    {
      year: "2020",
      phase: "Foundation Building",
      focus: "Web Development Fundamentals",
      skills: [
        { name: "HTML5", level: "Intermediate" },
        { name: "CSS3", level: "Intermediate" },
        { name: "JavaScript", level: "Beginner" },
        { name: "WordPress", level: "Intermediate" },
        { name: "PHP", level: "Beginner" },
        { name: "MySQL", level: "Beginner" }
      ]
    },
    {
      year: "2021",
      phase: "Frontend Specialization",
      focus: "Modern Frontend Development",
      skills: [
        { name: "React", level: "Intermediate" },
        { name: "JavaScript ES6+", level: "Advanced" },
        { name: "CSS3/Sass", level: "Advanced" },
        { name: "Bootstrap", level: "Advanced" },
        { name: "Git", level: "Intermediate" },
        { name: "Responsive Design", level: "Advanced" }
      ]
    },
    {
      year: "2022",
      phase: "Full-Stack Development",
      focus: "MERN Stack Mastery",
      skills: [
        { name: "React", level: "Advanced" },
        { name: "Node.js", level: "Advanced" },
        { name: "MongoDB", level: "Intermediate" },
        { name: "Express.js", level: "Advanced" },
        { name: "REST APIs", level: "Advanced" },
        { name: "AWS", level: "Intermediate" }
      ]
    },
    {
      year: "2023-2024",
      phase: "Senior Development",
      focus: "Architecture & Leadership",
      skills: [
        { name: "React 18", level: "Expert" },
        { name: "TypeScript", level: "Advanced" },
        { name: "Node.js", level: "Expert" },
        { name: "PostgreSQL", level: "Advanced" },
        { name: "Docker", level: "Advanced" },
        { name: "GraphQL", level: "Advanced" },
        { name: "AWS", level: "Advanced" },
        { name: "Microservices", level: "Advanced" }
      ]
    }
  ];

  // Mock technology evolution data
  const technologyEvolutionData = [
    {
      category: "Frontend Development",
      icon: "Monitor",
      timeline: [
        {
          year: "2020",
          technologies: [
            { name: "HTML5", status: "previous" },
            { name: "CSS3", status: "previous" },
            { name: "jQuery", status: "previous" }
          ]
        },
        {
          year: "2021",
          technologies: [
            { name: "React", status: "current" },
            { name: "Sass", status: "previous" },
            { name: "Bootstrap", status: "previous" }
          ]
        },
        {
          year: "2022-2024",
          technologies: [
            { name: "React 18", status: "current" },
            { name: "TypeScript", status: "current" },
            { name: "Tailwind CSS", status: "current" },
            { name: "Next.js", status: "learning" }
          ]
        }
      ]
    },
    {
      category: "Backend Development",
      icon: "Server",
      timeline: [
        {
          year: "2020",
          technologies: [
            { name: "PHP", status: "previous" },
            { name: "MySQL", status: "previous" }
          ]
        },
        {
          year: "2021-2022",
          technologies: [
            { name: "Node.js", status: "current" },
            { name: "Express.js", status: "current" },
            { name: "MongoDB", status: "current" }
          ]
        },
        {
          year: "2023-2024",
          technologies: [
            { name: "Node.js", status: "current" },
            { name: "PostgreSQL", status: "current" },
            { name: "GraphQL", status: "current" },
            { name: "Microservices", status: "learning" }
          ]
        }
      ]
    },
    {
      category: "DevOps & Cloud",
      icon: "Cloud",
      timeline: [
        {
          year: "2021",
          technologies: [
            { name: "Git", status: "current" },
            { name: "GitHub", status: "current" }
          ]
        },
        {
          year: "2022",
          technologies: [
            { name: "AWS EC2", status: "current" },
            { name: "AWS S3", status: "current" }
          ]
        },
        {
          year: "2023-2024",
          technologies: [
            { name: "Docker", status: "current" },
            { name: "AWS Lambda", status: "current" },
            { name: "CI/CD", status: "current" },
            { name: "Kubernetes", status: "learning" }
          ]
        }
      ]
    }
  ];

  const filteredExperiences = experiencesData.filter(exp => {
    if (activeFilter === 'all') return true;
    return exp.type.toLowerCase().includes(activeFilter);
  });

  const toggleCard = (id) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCards(newExpanded);
  };

  const scrollToExperience = (id) => {
    const element = document.getElementById(`experience-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Experience - Career Journey Timeline | CodeShow LapZ</title>
        <meta name="description" content="Explore the professional journey behind CodeShow LapZ through an interactive timeline showcasing Lahiru Senavirathna's 4+ years of full-stack development experience, key achievements, and technology evolution." />
        <meta name="keywords" content="Lahiru Senavirathna, career timeline, full-stack developer experience, professional journey, software development career" />
      </Helmet>

      <div className="min-h-screen bg-surface">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-20 sm:pt-24 pb-12 sm:pb-16 bg-gradient-to-br from-primary/5 via-white to-accent/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 text-primary rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                <Icon name="Briefcase" size={14} className="mr-2" />
                Professional Journey
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-text-primary mb-4 sm:mb-6 px-4">
                Career Journey
                <span className="gradient-text block">Timeline</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed px-4">
                Discover my professional evolution from junior developer to senior full-stack engineer. 
                Each role has shaped my expertise and contributed to my growth in the tech industry.
              </p>
            </div>

            {/* Career Stats */}
            <CareerStats stats={careerStatsData} />
          </div>
        </section>

        {/* Timeline Navigation */}
        <section className="py-6 sm:py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <TimelineNavigation
              experiences={filteredExperiences}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onScrollToExperience={scrollToExperience}
            />
          </div>
        </section>

        {/* Experience Timeline */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {viewMode === 'timeline' ? (
              <div className="relative">
                {/* Mobile Timeline Line */}
                <div className="lg:hidden absolute left-2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-400 via-blue-400 to-purple-400 opacity-50"></div>
                
                {filteredExperiences.map((experience, index) => (
                  <div key={experience.id} id={`experience-${experience.id}`}>
                    <TimelineCard
                      experience={experience}
                      index={index}
                      isExpanded={expandedCards.has(experience.id)}
                      onToggle={toggleCard}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {filteredExperiences.map((experience) => (
                  <div key={experience.id} id={`experience-${experience.id}`}>
                    <TimelineCard
                      experience={experience}
                      index={0}
                      isExpanded={expandedCards.has(experience.id)}
                      onToggle={toggleCard}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Skills Evolution */}
        <section className={`py-12 sm:py-16 ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-3 sm:mb-4 px-4">
                Skills Evolution Journey
              </h2>
              <p className="text-base sm:text-lg text-text-secondary max-w-2xl mx-auto px-4">
                Track how my technical skills have evolved and expanded throughout my career journey.
              </p>
            </div>
            <SkillEvolution skillsData={skillsEvolutionData} />
          </div>
        </section>

        {/* Technology Evolution */}
        <section className="py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-3 sm:mb-4 px-4">
                Technology Stack Evolution
              </h2>
              <p className="text-base sm:text-lg text-text-secondary max-w-2xl mx-auto px-4">
                See how my technology preferences and expertise have evolved across different domains.
              </p>
            </div>
            <TechnologyEvolution techData={technologyEvolutionData} />
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-12 sm:py-16 bg-gradient-to-r from-primary to-accent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
              Ready to Work Together?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Let's discuss how my experience and skills can contribute to your next project. I'm always excited to take on new challenges and create amazing solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Button
                variant="secondary"
                size="lg"
                iconName="Download"
                iconPosition="left"
                className="bg-white text-primary hover:bg-white/90"
              >
                Download Resume
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                className="border-white text-white hover:bg-white hover:text-primary"
              >
                Start Conversation
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ExperienceCareerJourneyTimeline;